import GetUserQuery from '~/queries/getUser.gql';
import ConnectAccountMutation from '~/mutations/ConnectAccountMutation.gql';
import DisconnectAccountMutation from '~/mutations/DisconnectAccountMutation.gql';
import * as ConnectedAccountHelpers from '../helpers/connected-account-helpers';
import Vue from 'vue';

type ApolloClient = Vue['$apolloProvider']['defaultClient'];

export type ConnectionName = 'epic_games' | 'steam';
const ConnectedAccountTypes: ConnectionName[] = ['epic_games', 'steam'];

export const isConnectedAccountType = (
  connectedAccountType: string,
): connectedAccountType is ConnectionName => {
  return (ConnectedAccountTypes as string[]).includes(connectedAccountType);
};

export type ConnectedAccountState =
  | { state: 'unknown' }
  | { state: 'ready-to-link' }
  | { state: 'linking' }
  | { state: 'unlinking' }
  | { state: 'error' }
  | { state: 'linked'; accountId: string }
  | { state: 'unlinked' };

interface IUserProfile {
  id: string;
  emailVerified: Date | undefined;
  connectedAccounts: Array<{
    connectionName: ConnectionName;
    accountId: string;
  }>;
}

export const getUserProfile = async (
  apolloClient: ApolloClient,
): Promise<IUserProfile | undefined> => {
  try {
    const getUserResponse = await apolloClient.query<{
      profile: IUserProfile;
    }>({
      query: GetUserQuery,
      fetchPolicy: 'network-only',
      errorPolicy: 'ignore',
    });

    if (getUserResponse.data?.profile) {
      return getUserResponse.data.profile;
    }
  } catch (e) {
    return undefined;
  }
};

export const getAccountId = (
  userProfile: IUserProfile,
  connectionName: ConnectionName,
) => {
  return (userProfile.connectedAccounts || []).find(
    connectedAccount => connectedAccount.connectionName === connectionName,
  )?.accountId;
};

export const getConnectionUri = (
  baseUrl: string | undefined,
  redirectUrl: string,
) => {
  const absoluteUrl = new URL(redirectUrl, window.location.href).href;

  return `${baseUrl || ''}&redirect_uri=${absoluteUrl.replace(
    /&?code=[A-Za-z0-9]+/,
    '',
  )}`;
};

export const connectAccount = async (
  context: {
    apolloClient: ApolloClient;
    setState: (state: ConnectedAccountState) => void;
    refreshUser: () => void;
    handleUnauthenticated: () => void;
    connectionUri: string;
  },
  args: {
    connectionName: ConnectionName;
    connectionUrl?: string;
    exchangeCode?: string;
    initiateConnection: boolean;
    requiresEmailVerification: boolean;
  },
) => {
  const userProfile = await getUserProfile(context.apolloClient);

  if (!userProfile) {
    return context.handleUnauthenticated();
  }

  const accountId = getAccountId(userProfile, args.connectionName);

  if (accountId) {
    context.setState({ state: 'linked', accountId });

    return;
  }

  const validIdentity = ConnectedAccountHelpers.isConnectedAccountType(
    args.connectionName,
  );

  if ((!args.initiateConnection && !args.exchangeCode) || !validIdentity) {
    context.setState({
      state: 'unlinked',
    });

    return;
  }

  if (!args.exchangeCode && args.connectionUrl) {
    context.setState({ state: 'ready-to-link' });

    window.location.href = context.connectionUri;

    return;
  }

  context.setState({
    state: 'linking',
  });

  const connectAccountResponse = await context.apolloClient.mutate<
    {
      connectAccount?: { connectionName: string; accountId: string };
    },
    { connectionName: string; exchangeCode: string }
  >({
    mutation: ConnectAccountMutation,
    variables: {
      connectionName: args.connectionName,
      exchangeCode: args.exchangeCode as string,
    },
    errorPolicy: 'ignore',
  });

  if (!connectAccountResponse.data?.connectAccount) {
    context.setState({ state: 'error' });

    return;
  }

  context.setState({
    state: 'linked',
    accountId: connectAccountResponse.data.connectAccount.accountId,
  });

  context.refreshUser();
};

export const disconnectAccount = async (
  context: {
    apolloClient: ApolloClient;
    setState: (state: ConnectedAccountState) => void;
    refreshUser: () => void;
  },
  args: { connectionName: ConnectionName; totpToken: string },
) => {
  context.setState({ state: 'unlinking' });

  await context.apolloClient.mutate<
    {
      disconnectAccount: {
        connectedAccounts: Array<{
          connectionName: string;
          accountId: string;
        }>;
      };
    },
    { connectionName: string; totpToken: string }
  >({
    mutation: DisconnectAccountMutation,
    variables: {
      connectionName: args.connectionName,
      totpToken: args.totpToken,
    },
  });

  context.refreshUser();

  context.setState({ state: 'unlinked' });
};
